/**
 * Shared image-anchor orientation heuristic.
 *
 * Legacy `.zip` scenes (and any scene the Creator app leaves without an
 * explicit `Horizontal`/`Vertical` label) come through with `Free`
 * orientation. For those, the marker's pose can't be read off the
 * orientation field — it has to be inferred from the authored content.
 *
 * This module is the single source of truth for that inference so the
 * two code paths that pose the scene root — {@link
 * ImageMarkerLoader.applyAnchorRotation} on load and
 * `ImageAnchor._applySceneRootRotation` on a mid-session re-commit — can
 * never disagree about what a `Free` marker means. (They used to: the
 * loader consulted the content heuristic while the behavior re-commit fell
 * straight through to the default pitch, so re-committing a `Free` table
 * marker silently flipped it to vertical.)
 *
 * Lives in its own dependency-free module (only `SceneObject` as a type
 * import + Babylon math + `AnchorOrientation`) so both `ImageMarkerLoader`
 * and the `ImageAnchor` behavior can import it without an `import/no-cycle`
 * violation (`ImageMarkerLoader` already value-imports `ImageAnchor`).
 */

import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { SceneObject } from '../core/SceneObject';
import { AnchorOrientation, resolveAnchorPitchRadians } from '../scenedata/common';

/**
 * Detect vertical (wall) vs horizontal (table) marker orientation from
 * the first child's local rotation.
 *
 * In the OpenXR anchor frame (post-loader conversion):
 *   - Horizontal-table content: local `+Y` aligned with anchor `+Z`
 *     (the normal axis) — model authored Y-up sits naturally on the
 *     table, perpendicular to it.
 *   - Vertical-wall content: local `+Y` aligned with anchor `+Y`
 *     (image-top, gravity-up for a wall marker) — the model was
 *     rotated in Unity so it stands upright against the wall.
 *
 * Test for vertical by checking whether the child's local-up direction
 * lands close to the image-top axis (`±Y`).
 *
 * Used as a fallback for legacy scenes that pre-date the explicit CMS
 * `imageOrientation` field. Modern scenes set orientation directly on
 * the ImageAnchor behavior.
 *
 * No children → vertical. With nothing to infer from, the marker stands
 * upright, which is how an editor shows a `Free` marker it attaches. Load
 * and attach must agree here: editor content (`attachCmsContent`) is
 * mounted under the host after it is posed, so a different load pose tips
 * all of it.
 */
export function detectVerticalFromChildren(children: SceneObject[]): boolean {
    if (children.length === 0) return true;

    const first = children[0];
    const q = first.transform.rotationQuaternion
        ?? Quaternion.FromEulerVector(first.transform.rotation);

    const up = Vector3.Up();
    up.applyRotationQuaternionInPlace(q);
    // Local +Y aligned with the anchor image-top axis (±Y) → vertical.
    return Math.abs(up.y) >= 0.7;
}

/**
 * Whether the authored fields leave the marker pose to the content
 * heuristic: `Free`/unset orientation with no non-zero pitch. The one
 * predicate behind {@link resolveAnchorPitchRadiansForRoot}, exported so
 * the localizer migration table (`legacyPlacementRotation`) marks exactly
 * the rows this resolver would hand to {@link detectVerticalFromChildren}.
 */
export function anchorPitchDependsOnContent(
    orientation: AnchorOrientation | undefined,
    pitchDegrees: number | undefined,
): boolean {
    const hasConcrete = orientation === AnchorOrientation.Vertical
        || orientation === AnchorOrientation.Horizontal;
    const hasNonZeroPitch = (pitchDegrees ?? 0) !== 0;
    return !hasConcrete && !hasNonZeroPitch;
}

/**
 * Resolve the scene-root pitch (radians about the marker's local X /
 * image-right axis) for the given orientation.
 *
 * This is the single decision every pose path shares — load
 * (`applyAnchorRotation`), a legacy-heuristic placement
 * (`resolveLegacyHeuristicRotation`) and re-commit (`_applySceneRootRotation`)
 * — so they can never disagree about what a `Free` marker means.
 *
 * - A concrete orientation (`Vertical`/`Horizontal`) or a non-zero authored
 *   pitch → {@link resolveAnchorPitchRadians}.
 * - `Free`/unset with pitch 0 → {@link detectVerticalFromChildren}:
 *   vertical → 0, horizontal → -π/2.
 */
export function resolveAnchorPitchRadiansForRoot(
    orientation: AnchorOrientation | undefined,
    pitchDegrees: number | undefined,
    children: SceneObject[],
): number {
    if (!anchorPitchDependsOnContent(orientation, pitchDegrees)) {
        return resolveAnchorPitchRadians(orientation, pitchDegrees);
    }
    return detectVerticalFromChildren(children) ? 0 : -Math.PI / 2;
}
